'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  const navigationItems = [
    { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { label: 'Our Story', href: '/our-story', icon: 'HeartIcon' },
    { label: 'Wedding Details', href: '/wedding-details', icon: 'CalendarIcon' },
    { label: 'RSVP', href: '/rsvp', icon: 'CheckCircleIcon' },
    { label: 'Gallery', href: '/gallery', icon: 'PhotoIcon' },
  ];

  const moreItems = [
    { label: 'Faith & Gratitude', href: '/faith-gratitude', icon: 'SparklesIcon' },
    { label: 'Support & Contributions', href: '/support-contributions', icon: 'GiftIcon' },
    { label: 'Wedding Dashboard', href: '/wedding-management-dashboard', icon: 'PresentationChartBarIcon' },
    { label: 'Family Dashboard', href: '/admin-login', icon: 'ChartBarIcon' },
    { label: 'Family Messages', href: '/family-communication-hub', icon: 'ChatBubbleLeftRightIcon' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isActive = (href: string) => pathname === href;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-card shadow-sacred-md' : 'bg-card'
        } ${className}`}
      >
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            {/* Logo */}
            <Link
              href="/homepage"
              className="flex items-center space-x-3 group"
              onClick={closeMobileMenu}
            >
              <div className="relative">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="transition-transform duration-300 group-hover:scale-105"
                >
                  <circle cx="20" cy="20" r="18" fill="var(--color-primary)" opacity="0.1" />
                  <path
                    d="M20 8C20 8 14 12 14 18C14 21.866 17.134 25 21 25C24.866 25 28 21.866 28 18C28 12 22 8 22 8"
                    stroke="var(--color-primary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20 25V32M16 32H24"
                    stroke="var(--color-secondary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="20" cy="18" r="3" fill="var(--color-accent)" />
                </svg>
              </div>
              <div className="hidden sm:block">
                <h1 className="font-playfair text-xl font-semibold text-foreground leading-none">
                  Moyak & Gloria
                </h1>
                <p className="font-inter text-xs text-muted-foreground mt-0.5">
                  Divine Timing
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`px-4 py-2 rounded-md font-inter text-sm font-medium transition-all duration-300 ${
                    isActive(item.href)
                      ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-surface'
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              {/* More Dropdown */}
              <div className="relative group">
                <button className="px-4 py-2 rounded-md font-inter text-sm font-medium text-foreground hover:text-primary hover:bg-surface transition-all duration-300 flex items-center space-x-1">
                  <span>More</span>
                  <Icon name="ChevronDownIcon" size={16} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                <div className="absolute right-0 mt-2 w-56 bg-card rounded-lg shadow-sacred-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-right">
                  <div className="py-2">
                    {moreItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`flex items-center space-x-3 px-4 py-2.5 font-inter text-sm transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-surface'
                        }`}
                      >
                        <Icon name={item.icon as any} size={18} />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-foreground hover:bg-surface transition-colors duration-200"
              aria-label="Toggle menu"
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-background z-40 lg:hidden"
          style={{ top: '64px' }}
        >
          <nav className="flex flex-col h-full overflow-y-auto">
            <div className="flex-1 px-4 py-6 space-y-1">
              {[...navigationItems, ...moreItems].map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-inter text-base font-medium transition-all duration-200 ${
                    isActive(item.href)
                      ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-surface'
                  }`}
                >
                  <Icon name={item.icon as any} size={20} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            {/* Mobile Menu Footer */}
            <div className="border-t border-border px-4 py-6">
              <p className="font-playfair text-sm text-center text-muted-foreground italic">
                "Love is patient, love is kind"
              </p>
              <p className="font-inter text-xs text-center text-muted mt-1">
                1 Corinthians 13:4
              </p>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;